import React, { useEffect, useState, useMemo, useRef } from "react";
import { Tabs, Tab, Box, Button, Tooltip, Typography } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { typographyScale } from "../../../../../../../styles/theme";
import {
  useMrtSurfaceProps,
  mrtHeadCellSx,
  mrtBodyCellSx,
} from "../../../../../../../styles/mrtThemeProps";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { columns, tabLabels } from "./columns";
import TotalSummaryTable from "./TotalSummaryTable";
import useSpecOptions from "./useSpecOptions";
import {
  UNITS_ENUM,
  updateDerivedFields,
  parseDimension,
  generateSummary,
  generateBOM,
  estimateStructuralRows,
} from "./utils";

import { secondAxios } from "../../../../../../../api/axios";

import { useStructureModeling } from "../../structureModelingContext";

const AREA_COMPONENTS = ["Grating", "Plate"];
const SPECIAL_COMPONENTS = ["Grating", "Plate", "Bolt", "Nut", "Washer"];
const STRUCT_UNIT_COLS = [
  { label: "Unit Weight", imp: "lb/ft", met: "kg/m" },
  { label: "Total Weight", imp: "lb", met: "kg" },
  { label: "Unit SA", imp: "ft²/ft", met: "m²/m" },
  { label: "Total SA", imp: "ft²", met: "m²" },
  { label: "Fab Rate", imp: "hrs/lb", met: "hrs/kg" },
  { label: "Painted", imp: "ft²", met: "m²" },
  { label: "Galvanized", imp: "ft²", met: "m²" },
  { label: "Fire Proofed", imp: "ft²", met: "m²" },
  { label: "BOM Weight", imp: "lb", met: "kg" },
];


// The two panels sit side by side and must line up row-for-row, so every
// vertical metric that affects alignment is fixed and shared between them:
// same header height, same row height, same viewport height. Anything that
// lets one panel's row grow (wrapping text, a taller control) breaks the
// alignment that the synced scrolling depends on.
const PANEL_ROW_HEIGHT = 45;
const PANEL_HEAD_HEIGHT = 56;
const PANEL_BODY_MAX_HEIGHT = 470;

// Estimated values only ever shows short numbers and three checkboxes, so it
// gets a fixed width — just enough for its columns at their declared sizes.
// Everything left over goes to the input panel, whose text needs the room.
const ESTIMATED_PANEL_WIDTH = 560;

// Every cell — plain text, text inputs and comboboxes — renders at this size.
const PANEL_FONT_SIZE = "0.65rem";

/*
 * MRT merges cell props with a shallow Object.assign, so a column's own
 * `muiTableBodyCellProps.sx` REPLACES the table-level one rather than
 * merging into it. Several structural columns carry their own sx/align, which
 * means table-level cell styling never reaches them. So the shared panel
 * styling has to be folded into each column definition instead.
 */
const panelBodyCellSx = (isFirstPanel) => ({
  ...mrtBodyCellSx,
  // No vertical rule between columns — the spacing alone separates them.
  borderRight: "none",
  height: `${PANEL_ROW_HEIGHT}px`,
  maxHeight: `${PANEL_ROW_HEIGHT}px`,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: isFirstPanel ? "4px" : "2px",
  paddingRight: isFirstPanel ? "4px" : "2px",
  boxSizing: "border-box",
  minWidth: 0,
  maxWidth: "none",
  // One line per cell. Without this a long Material Description wraps and
  // makes just that row taller than its opposite number in the other panel.
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "center",
  fontSize: PANEL_FONT_SIZE,
  lineHeight: 1.2,

  // Keep every control short enough to sit inside the fixed row height, and
  // pin them all to one font size so text, inputs and comboboxes match.
  "& .MuiInputBase-root": {
    minHeight: "26px",
    height: "26px",
    fontSize: `${PANEL_FONT_SIZE} !important`,
  },
  "& .MuiInputBase-input": {
    paddingTop: "2px",
    paddingBottom: "2px",
    fontSize: `${PANEL_FONT_SIZE} !important`,
  },
  "& .MuiSelect-select": {
    minHeight: "unset !important",
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
    display: "flex",
    alignItems: "center",
    fontSize: `${PANEL_FONT_SIZE} !important`,
    // EditableSelectCell asks for wrapping text; inside a fixed-height row
    // it has to stay on one line instead.
    whiteSpace: "nowrap !important",
    overflow: "hidden !important",
    textOverflow: "ellipsis",
    wordBreak: "normal !important",
  },
  "& .MuiSelect-icon": { fontSize: "0.9rem", right: 0 },
  "& .MuiButton-root": {
    minHeight: "26px",
    height: "26px",
    fontSize: `${PANEL_FONT_SIZE} !important`,
  },
  "& .MuiIconButton-root": { padding: "2px" },
  "& .MuiCheckbox-root": { padding: "2px" },
  "& .MuiSvgIcon-root": { fontSize: "1rem" },
});

// A fixed header height in both panels. The estimated columns' labels run to
// two lines (name + unit) and the input columns' to one — left alone, the two
// bodies would start at different offsets and no amount of scroll syncing
// would line the rows up.
const panelHeadCellSx = {
  ...mrtHeadCellSx,
  fontWeight: "bold",
  fontSize: PANEL_FONT_SIZE,
  borderRight: "none",
  height: `${PANEL_HEAD_HEIGHT}px`,
  maxHeight: `${PANEL_HEAD_HEIGHT}px`,
  paddingTop: "2px",
  paddingBottom: "2px",
  paddingLeft: "4px",
  paddingRight: "4px",
  verticalAlign: "middle",
  boxSizing: "border-box",
  minWidth: 0,
  maxWidth: "none",
  "& .Mui-TableHeadCell-Content": {
    justifyContent: "center",
    height: "100%",
    alignItems: "center",
  },
  "& .Mui-TableHeadCell-Content-Wrapper": {
    whiteSpace: "normal",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.15,
    width: "100%",
    textAlign: "center",
  },
};

// Fold the shared panel styling into each column, letting it win over the
// column's own sx for the keys that decide alignment.
function withPanelCellStyles(tableColumns, isFirstPanel) {
  const bodySx = panelBodyCellSx(isFirstPanel);

  return tableColumns.map((col) => ({
    ...col,
    muiTableBodyCellProps: {
      ...(col.muiTableBodyCellProps || {}),
      sx: { ...(col.muiTableBodyCellProps?.sx || {}), ...bodySx },
    },
    muiTableHeadCellProps: {
      ...(col.muiTableHeadCellProps || {}),
      sx: { ...(col.muiTableHeadCellProps?.sx || {}), ...panelHeadCellSx },
    },
  }));
}

// Columns that stay in the input panel vs. the ones Estimate fills in.
// `actions` stays on the left so a row can always be deleted, even before
// Estimate has been run and revealed the right-hand panel.
const FIRST_PANEL_KEYS = [
  "Spec",
  "Components",
  "Size",
  "Material",
  "Material Description",
  "Requirements",
  "Qty",
  "length",
  "Unit",
  "actions",
];

const SECOND_PANEL_KEYS = [
  "UnitWeight",
  "totalWeight",
  "UnitSA",
  "totalSA",
  "FabRate",
  "totalFabHrs",
  "Painted",
  "Galvanized",
  "FireProofed",
];

// ─── Unit helpers ─────────────────────────────────────────────────────────────
function getUnitForComponent(componentName, isMetric) {
  if (!componentName) return isMetric ? "m" : "ft";
  if (AREA_COMPONENTS.includes(componentName)) return isMetric ? "m²" : "ft²";
  if (SPECIAL_COMPONENTS.includes(componentName)) return "Nos";
  return isMetric ? "m" : "ft";
}

// ─── Conversion helpers ───────────────────────────────────────────────────────
function toMetricWeight(val) {
  const n = Number(val);
  return isNaN(n) || !n ? val : +(n * 0.453592).toFixed(4);
}
function toMetricArea(val) {
  const n = Number(val);
  return isNaN(n) || !n ? val : +(n * 0.092903).toFixed(4);
}
function toMetricFabRate(val) {
  const n = Number(val);
  return isNaN(n) || !n ? val : +(n / 0.453592).toFixed(4);
}
function toImperialWeight(val) {
  const n = Number(val);
  return isNaN(n) || !n ? val : +(n / 0.453592).toFixed(4);
}
function toImperialArea(val) {
  const n = Number(val);
  return isNaN(n) || !n ? val : +(n / 0.092903).toFixed(4);
}
function toImperialFabRate(val) {
  const n = Number(val);
  return isNaN(n) || !n ? val : +(n * 0.453592).toFixed(4);
}

function enrichStructuralRows(allTables, selectedSpecsTable, rows, isMetric) {
  return (rows || []).map((r, idx) => {
    const next = { ...r };

    try {
      updateDerivedFields(
        allTables,
        selectedSpecsTable,
        (rowIndex, key, value) => {
          next[key] = value;
        },
        idx,
        next,
      );
    } catch (e) {
      console.error("enrichStructuralRows failed", e);
    }

    const qty = Number(next.Qty ?? 0) || 0;
    const dim = parseDimension(next.length);
    const uw = Number(next.UnitWeight ?? 0) || 0;
    const fabRate = Number(next.FabRate ?? 0) || 0;
    const usa = Number(next.UnitSA ?? 0) || 0;

    if (!qty) {
      next.totalWeight = undefined;
      next.totalSA = undefined;
      next.totalFabHrs = undefined;
      return next;
    }

    const isAreaBased = AREA_COMPONENTS.includes(next.Components);
    const unitWeight = isMetric ? toMetricWeight(uw) : uw;
    const unitSA = isMetric ? toMetricArea(usa) : usa;

    if (isAreaBased) {
      const totalW = qty && dim && unitWeight ? qty * dim * unitWeight : 0;
      next.totalWeight = totalW ? Number(totalW.toFixed(2)) : "";
      const totalSA = qty && dim ? qty * dim : 0;
      next.totalSA = totalSA ? Number(totalSA.toFixed(2)) : "";
      const totalFab = totalW && fabRate ? totalW * fabRate : 0;
      next.totalFabHrs = totalFab ? Number(totalFab.toFixed(2)) : "";
    } else {
      const totalW = qty && dim && unitWeight ? qty * dim * unitWeight : 0;
      next.totalWeight = totalW ? Number(totalW.toFixed(2)) : "";
      const totalSA = qty && dim && unitSA ? qty * dim * unitSA : 0;
      next.totalSA = totalSA ? Number(totalSA.toFixed(2)) : "";
      const totalFab = totalW && fabRate ? totalW * fabRate : 0;
      next.totalFabHrs = totalFab ? Number(totalFab.toFixed(2)) : "";
    }

    return next;
  });
}

function computeTotals(enrichedRows) {
  return (enrichedRows || []).reduce(
    (acc, r) => {
      acc.totalWeight += Number(r.totalWeight || 0) || 0;
      acc.totalFabHrs += Number(r.totalFabHrs || 0) || 0;
      const tsa = Number(r.totalSA || 0) || 0;
      if (r.Painted) acc.paintArea += tsa;
      if (r.Galvanized) acc.galvArea += tsa;
      if (r.FireProofed) acc.fireArea += tsa;
      return acc;
    },
    { totalWeight: 0, totalFabHrs: 0, paintArea: 0, galvArea: 0, fireArea: 0 },
  );
}

function UnitToggle({ isMetric, onChange }) {
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Box
        sx={{
          fontSize: "0.7rem",
          color: "text.secondary",
          mb: 0.75,
          fontWeight: 500,
        }}
      >
        Columns that change
      </Box>
      {STRUCT_UNIT_COLS.map((c) => (
        <Box
          key={c.label}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            fontSize: "0.7rem",
            py: "2px",
          }}
        >
          <span style={{ color: "inherit", opacity: 0.75 }}>{c.label}</span>
          <span style={{ fontWeight: 500 }}>{isMetric ? c.met : c.imp}</span>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="bottom-start" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          border: "0.5px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          ml: 1,
          cursor: "default",
        }}
      >
        <Box
          onClick={() => onChange(false)}
          sx={{
            px: 1.5,
            py: 0.6,
            fontSize: typographyScale.fontSize.sm,
            fontWeight: isMetric ? 400 : 500,
            color: isMetric ? "text.secondary" : "primary.main",
            bgcolor: isMetric ? "transparent" : "action.selected",
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: isMetric ? "action.hover" : "action.selected",
            },
          }}
        >
          Imperial
          <Box
            component="span"
            sx={{
              fontSize: typographyScale.fontSize.xs,
              px: 0.75,
              py: "1px",
              borderRadius: 99,
              bgcolor: isMetric ? "transparent" : "action.selected",
              color: isMetric ? "text.disabled" : "primary.main",
              fontWeight: 500,
              transition: "all 0.15s",
            }}
          >
            ft · lb
          </Box>
        </Box>
        <Box sx={{ width: "0.5px", height: 20, bgcolor: "divider" }} />
        <Box
          onClick={() => onChange(true)}
          sx={{
            px: 1.5,
            py: 0.6,
            fontSize: typographyScale.fontSize.sm,
            fontWeight: isMetric ? 500 : 400,
            color: isMetric ? "primary.main" : "text.secondary",
            bgcolor: isMetric ? "action.selected" : "transparent",
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: isMetric ? "action.selected" : "action.hover",
            },
          }}
        >
          Metric
          <Box
            component="span"
            sx={{
              fontSize: typographyScale.fontSize.xs,
              px: 0.75,
              py: "1px",
              borderRadius: 99,
              bgcolor: isMetric ? "action.selected" : "transparent",
              color: isMetric ? "success.dark" : "text.disabled",
              fontWeight: 500,
              transition: "all 0.15s",
            }}
          >
            m · kg
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}

export function EstimationTable() {
  const mrtSurfaceProps = useMrtSurfaceProps();
  const [tabIndex, setTabIndex] = useState(0);
  const [isMetric, setIsMetric] = useState(false);
  const [selectedSpecsTable, setSelectedSpecsTable] = useState([]);
  const currentTab = tabLabels[tabIndex];

  const [showEstimatedValues, setShowEstimatedValues] = useState(false);

  // ─── Synced vertical scrolling between the two side-by-side panels ─────────
  // Both tables render the same rows at the same fixed row height, so mirroring
  // scrollTop keeps a row on the left visually level with its estimated values
  // on the right. The guard stops the mirrored scroll from echoing back.
  const firstPanelScrollRef = useRef(null);
  const secondPanelScrollRef = useRef(null);
  const isSyncingScrollRef = useRef(false);

  const syncPanelScroll = (sourceRef, targetRef) => () => {
    if (isSyncingScrollRef.current) return;
    const source = sourceRef.current;
    const target = targetRef.current;
    if (!source || !target || source.scrollTop === target.scrollTop) return;

    isSyncingScrollRef.current = true;
    target.scrollTop = source.scrollTop;
    // Release on the next frame, after the mirrored scroll event has fired.
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  };

  const {
    getSelectedSpecTable,
    selectedSpecs,
    allTables,
    estimationTable,
    setEstimationTable,
    totalSummary,
    setTotalSummary,
    selectedEstimationRevision,
    allCombinationTable,
    markCombinationRowSelected,
  } = useStructureModeling();

  const { id: projectId } = useParams();

  // Always start each revision with Estimated values hidden, even if saved
  // estimation values already exist in state. It is shown only by Estimate.
  useEffect(() => {
    setShowEstimatedValues(false);
  }, [selectedEstimationRevision]);

  // ─── Dropdown options sourced from the whole Spec Library ────────────────────
  const { getOptions, applySelection } = useSpecOptions(allCombinationTable);

  const api = useMemo(
    () => ({
      getItems: () =>
        estimationTable[selectedEstimationRevision]?.[currentTab] || [],
    }),
    [estimationTable, selectedEstimationRevision, currentTab],
  );

  const currentRows = useMemo(
    () => estimationTable?.[selectedEstimationRevision]?.[currentTab] || [],
    [estimationTable, selectedEstimationRevision, currentTab],
  );

  useEffect(() => {
    setSelectedSpecsTable((selectedSpecs || []).flatMap(getSelectedSpecTable));
  }, [selectedSpecs, getSelectedSpecTable]);

  // ─── Toggle-level unit conversion ────────────────────────────────────────────
  function handleUnitToggle(newIsMetric) {
    if (newIsMetric === isMetric) return;

    const toMetric = newIsMetric;

    const convertRow = (row) => {
      if (
        SPECIAL_COMPONENTS.includes(row.Components) &&
        !AREA_COMPONENTS.includes(row.Components)
      )
        return row;
      const next = { ...row };

      next.Unit = getUnitForComponent(next.Components, toMetric);

      if (next.UnitWeight !== undefined && next.UnitWeight !== "") {
        next.UnitWeight = toMetric
          ? toMetricWeight(next.UnitWeight)
          : toImperialWeight(next.UnitWeight);
      }
      if (next.UnitSA !== undefined && next.UnitSA !== "") {
        next.UnitSA = toMetric
          ? toMetricArea(next.UnitSA)
          : toImperialArea(next.UnitSA);
      }
      if (next.FabRate !== undefined && next.FabRate !== "") {
        next.FabRate = toMetric
          ? toMetricFabRate(next.FabRate)
          : toImperialFabRate(next.FabRate);
      }
      if (next.totalWeight !== undefined && next.totalWeight !== "") {
        next.totalWeight = toMetric
          ? toMetricWeight(next.totalWeight)
          : toImperialWeight(next.totalWeight);
      }
      if (next.totalSA !== undefined && next.totalSA !== "") {
        next.totalSA = toMetric
          ? toMetricArea(next.totalSA)
          : toImperialArea(next.totalSA);
      }

      return next;
    };

    setEstimationTable((prev) => {
      const rev = selectedEstimationRevision;
      const revData = prev?.[rev] || {};

      const freshEstimation = (revData.Estimation || []).map(convertRow);

      const freshSummary = (revData.Summary || []).map((r) => {
        if (r.isSummary) return r;
        const newUnit = getUnitForComponent(r.Components, toMetric);
        if (r.Unit === newUnit) return r;
        return { ...r, Unit: newUnit };
      });

      return {
        ...prev,
        [rev]: {
          ...revData,
          Estimation: freshEstimation,
          Summary: freshSummary,
        },
      };
    });

    setIsMetric(newIsMetric);
  }

  // ─── Fetch & merge unitCost when switching to Summary tab ───────────────────
  useEffect(() => {
    if (currentTab !== "Summary") return;

    const summaryRows =
      estimationTable?.[selectedEstimationRevision]?.Summary || [];
    const nonSummaryRows = summaryRows.filter(
      (r) => !r.isSummary && r["Material Description"],
    );
    if (!nonSummaryRows.length) return;

    const fetchAndMerge = async () => {
      try {
        const { data: materialRecords } =
          await secondAxios.get("/material-record");

        setEstimationTable((prev) => {
          const rev = selectedEstimationRevision;
          const existing = prev?.[rev]?.Summary || [];

          const merged = existing.map((r) => {
            if (r.isSummary || !r["Material Description"]) return r;
            const match = materialRecords?.find((mr) =>
              mr.description?.includes(r["Material Description"]),
            );
            return {
              ...r,
              unitCost: match?.estCost ?? r.unitCost ?? "Not Added!",
            };
          });

          return { ...prev, [rev]: { ...prev?.[rev], Summary: merged } };
        });
      } catch (e) {
        console.error("Failed to fetch material records", e);
      }
    };

    fetchAndMerge();
  }, [currentTab, selectedEstimationRevision]);

  // ─── Sync Total Materials Cost whenever Summary rows change ─────────────────
  useEffect(() => {
    const rows = (
      estimationTable?.[selectedEstimationRevision]?.Summary || []
    ).filter((r) => !r.isSummary);

    const total = rows.reduce((sum, r) => {
      const tc =
        r.procuredQty &&
        r.procuredQty !== "-" &&
        r.unitCost &&
        r.unitCost !== "Not Added!"
          ? Number(r.procuredQty) * Number(r.unitCost)
          : 0;
      return sum + (Number(tc) || 0);
    }, 0);

    setTotalSummary((prev) =>
      (prev || []).map((x) =>
        x.name === "Total Materials Cost"
          ? { ...x, value: total > 0 ? total.toFixed(2) : "" }
          : x,
      ),
    );
  }, [estimationTable, selectedEstimationRevision]);

  // ─── Handlers ────────────────────────────────────────────────────────────────
  function handleDeleteRow(rowIndex) {
    setEstimationTable((prev) => {
      const rev = selectedEstimationRevision;
      const updatedRevision = { ...(prev?.[rev] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      updatedTab.splice(rowIndex, 1);
      return {
        ...prev,
        [rev]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  function handleFieldUpdate(rowIndex, fieldName, value) {
    setEstimationTable((prev) => {
      const rev = selectedEstimationRevision;
      const updatedRevision = { ...(prev?.[rev] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      updatedTab[rowIndex] = { ...updatedTab[rowIndex], [fieldName]: value };
      return {
        ...prev,
        [rev]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  // ─── Estimate ────────────────────────────────────────────────────────────────
  async function handleEstimate() {
    const rev = selectedEstimationRevision;

    let materialRecords = [];
    try {
      const { data } = await secondAxios.get("/material-record");
      materialRecords = data || [];
    } catch (e) {
      console.error("Failed to fetch material records", e);
    }

    setEstimationTable((prev) => {
      const revData = prev?.[rev] || {};
      let estimationRows = revData.Estimation || [];

      if (!estimationRows.length) {
        return prev;
      }

      // First, enrich the rows using the existing enrichStructuralRows function
      // Convert to imperial if metric is active
      const rawRows = isMetric
        ? estimationRows.map((row) => {
            if (
              SPECIAL_COMPONENTS.includes(row.Components) &&
              !AREA_COMPONENTS.includes(row.Components)
            )
              return row;
            const next = { ...row };
            if (next.UnitWeight !== undefined && next.UnitWeight !== "")
              next.UnitWeight = toImperialWeight(next.UnitWeight);
            if (next.UnitSA !== undefined && next.UnitSA !== "")
              next.UnitSA = toImperialArea(next.UnitSA);
            if (next.FabRate !== undefined && next.FabRate !== "")
              next.FabRate = toImperialFabRate(next.FabRate);
            next.Unit = getUnitForComponent(next.Components, false);
            return next;
          })
        : [...estimationRows];

      // Enrich rows (this computes totalWeight, totalSA, totalFabHrs)
      const enriched = enrichStructuralRows(
        allTables,
        selectedSpecsTable,
        rawRows,
        false, // Always enrich in imperial
      );

      // Filter rows with qty > 0 for summary
      const rowsWithQty = enriched.filter((r) => (Number(r.Qty) || 0) > 0);

      // Generate Summary from enriched rows
      const summary = generateSummary(rowsWithQty, revData.Summary);

      // Merge unitCost from material records
      const summaryWithCost = summary.map((r) => {
        if (r.isSummary || !r["Material Description"]) return r;
        const match = materialRecords.find((mr) =>
          mr.description?.includes(r["Material Description"]),
        );
        return {
          ...r,
          unitCost: match?.estCost ?? r.unitCost ?? "Not Added!",
        };
      });

      // Generate BOM from summary
      const bom = generateBOM(summaryWithCost);

      // Compute totals for the Total Summary Table
      const totals = rowsWithQty.reduce(
        (acc, r) => {
          acc.totalWeight += Number(r.totalWeight || 0) || 0;
          acc.totalFabHrs += Number(r.totalFabHrs || 0) || 0;
          const tsa = Number(r.totalSA || 0) || 0;
          if (r.Painted) acc.paintArea += tsa;
          if (r.Galvanized) acc.galvArea += tsa;
          if (r.FireProofed) acc.fireArea += tsa;
          return acc;
        },
        {
          totalWeight: 0,
          totalFabHrs: 0,
          paintArea: 0,
          galvArea: 0,
          fireArea: 0,
        },
      );

      // Calculate total materials cost from summary
      const materialsTotal = summaryWithCost
        .filter((r) => !r.isSummary)
        .reduce((sum, r) => {
          const tc =
            r.procuredQty &&
            r.procuredQty !== "-" &&
            r.unitCost &&
            r.unitCost !== "Not Added!"
              ? Number(r.procuredQty) * Number(r.unitCost)
              : 0;
          return sum + (Number(tc) || 0);
        }, 0);

      // Update totalSummary state
      setTotalSummary((prevTS) =>
        (prevTS || []).map((x) => {
          if (x.name === "Total Materials Cost")
            return {
              ...x,
              value: "",
              total: materialsTotal > 0 ? materialsTotal.toFixed(2) : "",
            };
          if (x.name === "Total Man Hours")
            return {
              ...x,
              value: totals.totalFabHrs.toFixed(2),
              total: x.rate
                ? (Number(totals.totalFabHrs) * Number(x.rate)).toFixed(2)
                : x.total || "",
            };
          if (x.name === "Painting Cost")
            return {
              ...x,
              value: totals.paintArea.toFixed(2),
              total: x.rate
                ? (Number(totals.paintArea) * Number(x.rate)).toFixed(2)
                : x.total || "",
            };
          if (x.name === "Galvanized Cost")
            return {
              ...x,
              value: totals.galvArea.toFixed(2),
              total: x.rate
                ? (Number(totals.galvArea) * Number(x.rate)).toFixed(2)
                : x.total || "",
            };
          if (x.name === "Fire Proof Cost")
            return {
              ...x,
              value: totals.fireArea.toFixed(2),
              total: x.rate
                ? (Number(totals.fireArea) * Number(x.rate)).toFixed(2)
                : x.total || "",
            };
          return x;
        }),
      );

      console.log("Estimate complete - Summary rows:", summaryWithCost.length);
      console.log("BOM rows:", bom.length);

      return {
        ...prev,
        [rev]: {
          ...revData,
          Estimation: enriched,
          Summary: summaryWithCost,
          BOM: bom,
        },
      };
    });

    // Estimated values must only become visible after the user clicks Estimate.
    setShowEstimatedValues(true);

    // Reset toggle to imperial after estimate
    setIsMetric(false);
  }

  function handleSendBomToPlm() {
    const bom = estimationTable[selectedEstimationRevision]?.BOM || [];
    const payload = {
      source: "2D Drawings",
      softwareId: uuidv4(),
      name: "Structure",
      dwgNumber: "001",
      dwgTitle: "Structure BOM",
      revision: selectedEstimationRevision,
      version: "673",
      sheetNumber: "0",
      sheetTitle: "Sheet",
      projectId: projectId,
      materials: bom.map((cur) => ({
        item: cur.ItemNo,
        ref: null,
        description: cur["Material Description"],
        length: cur.Unit === UNITS_ENUM.Ft ? cur.Qty : null,
        quantity: cur.Unit === UNITS_ENUM.Ft ? 1 : cur.Qty,
        weightlb: cur.Weight,
        costCode: null,
        softwareId: uuidv4(),
      })),
    };
    secondAxios.delete("drawing/title/Structure BOM");
    secondAxios.post("drawing", [payload]);
  }

  /*
   * A cascade dropdown changed. useSpecOptions owns clearing the fields below
   * the changed one, autofilling, and setting `select` / `__specRow`.
   * UnitWeight, UnitSA, MaterialRate and FabRate are left to the existing
   * enrichStructuralRows → updateDerivedFields pipeline.
   */
  function handleSpecSelect(rowIndex, field, value) {
    const currentRow =
      estimationTable?.[selectedEstimationRevision]?.Estimation?.[rowIndex];
    if (!currentRow || currentRow.isSummary) return;

    const updated = applySelection(currentRow, field, value);

    setEstimationTable((prev) => {
      const revData = prev?.[selectedEstimationRevision] || {};
      const rows = [...(revData.Estimation || [])];
      if (!rows[rowIndex] || rows[rowIndex].isSummary) return prev;

      rows[rowIndex] = updated;

      return {
        ...prev,
        [selectedEstimationRevision]: { ...revData, Estimation: rows },
      };
    });

    /*
     * Flag the row inside allCombinationTable too — SelectedParts filters on
     * `select`, so flagging only the copy carried on the Estimation row is not
     * enough.
     */
    if (updated.__specRow) markCombinationRowSelected(updated.__specRow);
  }

  /*
   * Insert a blank row above any trailing summary rows so totals stay at the
   * bottom.
   */
  function handleAddRow() {
    setEstimationTable((prev) => {
      const revData = prev?.[selectedEstimationRevision] || {};
      const rows = [...(revData.Estimation || [])];

      const blank = {
        Spec: "",
        Components: "",
        Size: "",
        Material: "",
        "Material Description": "",
        Qty: 1,
        select: false,
        __specRow: null,
      };

      const firstSummaryIndex = rows.findIndex((r) => r?.isSummary);
      if (firstSummaryIndex === -1) {
        rows.push(blank);
      } else {
        rows.splice(firstSummaryIndex, 0, blank);
      }

      return {
        ...prev,
        [selectedEstimationRevision]: { ...revData, Estimation: rows },
      };
    });
  }

  function handleSummaryRateUpdate(targetCol, value) {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[selectedEstimationRevision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      const rateRowIndex = updatedTab.length - 3;
      if (rateRowIndex < 0 || !updatedTab[rateRowIndex]?.isSummary) return prev;
      updatedTab[rateRowIndex] = {
        ...updatedTab[rateRowIndex],
        [targetCol]: value,
      };
      return {
        ...prev,
        [selectedEstimationRevision]: {
          ...updatedRevision,
          [currentTab]: updatedTab,
        },
      };
    });
  }


  // ─── Two side-by-side panels ────────────────────────────────────────────────
  // Estimation splits into an input panel (what the user picks and types) and
  // an "Estimated values" panel (what Estimate computes). Both render the same
  // rows at the same fixed height so their scrolling can be mirrored.
  // Nothing is shown or editable until a revision is confirmed in the header.
  const hasRevision = Boolean(selectedEstimationRevision);

  const allEstimationColumns = columns(
    allTables,
    selectedSpecsTable,
    handleFieldUpdate,
    handleDeleteRow,
    handleSummaryRateUpdate,
    totalSummary,
    setTotalSummary,
    isMetric,
    getOptions,
    handleSpecSelect,
  ).Estimation;

  const firstPanelColumns = withPanelCellStyles(
    allEstimationColumns.filter((column) =>
      FIRST_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    true,
  );

  const secondPanelColumns = withPanelCellStyles(
    allEstimationColumns.filter((column) =>
      SECOND_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    false,
  );

  const renderEstimationPanel = (
    tableColumns,
    tableData,
    label,
    scrollRef,
    onScroll,
  ) => {
    // Both panels use identical vertical metrics so their rows stay level.
    const isFirstPanel = label !== "Estimated values";

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          // Estimated values is pinned to just enough width for its numeric
          // columns; the input panel takes everything that's left.
          ...(isFirstPanel
            ? { flex: "1 1 0" }
            : {
                flex: `0 0 ${ESTIMATED_PANEL_WIDTH}px`,
                width: `${ESTIMATED_PANEL_WIDTH}px`,
              }),
          minWidth: 0,
        }}
      >
        {label && (
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.main", fontWeight: 600, px: 1, py: 0.5 }}
          >
            {label}
          </Typography>
        )}

        <MaterialReactTable
          columns={tableColumns}
          data={tableData}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableStickyHeader={true}
          enableSorting={false}
          enableColumnActions={false}
          {...mrtSurfaceProps}

          /*
           * tableLayout: fixed + width: 100% makes the browser scale the
           * columns' declared sizes to exactly fill the panel — so the columns
           * are never spread apart by leftover space, and never overflow into
           * a horizontal scrollbar either.
           */
          muiTableProps={{ sx: { tableLayout: "fixed", width: "100%" } }}

          muiTableContainerProps={{
            ref: scrollRef,
            onScroll,
            sx: {
              height: PANEL_BODY_MAX_HEIGHT,
              maxHeight: PANEL_BODY_MAX_HEIGHT,
              overflowX: "hidden",
              backgroundColor: "background.paper",
            },
          }}

          /*
           * Same fixed row height in both panels — this is what lets the
           * synced scroll keep matching rows on the same visual line.
           */
          muiTableBodyRowProps={{
            sx: { height: `${PANEL_ROW_HEIGHT}px !important` },
          }}

          /*
           * These only apply to columns that carry no sx of their own — every
           * real column gets the same styling folded in by withPanelCellStyles
           * above, because MRT's shallow prop merge would otherwise drop it.
           */
          muiTableBodyCellProps={{ sx: panelBodyCellSx(isFirstPanel) }}
          muiTableHeadCellProps={{ sx: panelHeadCellSx }}

          muiTableHeadRowProps={{
            sx: {
              backgroundColor: "background.default",
              height: `${PANEL_HEAD_HEIGHT}px`,
            },
          }}

          initialState={{ pagination: { pageSize: 1000, pageIndex: 0 } }}

          state={{
            columnOrder: tableColumns.map((col) => col.accessorKey || col.id),
          }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Tabs value={tabIndex} onChange={(e, newIndex) => setTabIndex(newIndex)} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        {tabLabels.map((label) => (
          <Tab label={label} key={label} />
        ))}
      </Tabs>

      <Box
        sx={{
          width: currentTab !== "Estimation" ? "850px" : "100%",
          px: 0,
          mt: 1,
        }}
      >
        <Box
          sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%" }}
        >
          {currentTab === "Estimation" && (
            <Button
              variant="contained"
              onClick={handleEstimate}
              disabled={!hasRevision}
            >
              Estimate
            </Button>
          )}
          {currentTab === "Estimation" && (
            <Button
              variant="outlined"
              onClick={handleAddRow}
              disabled={!hasRevision}
            >
              Add Row
            </Button>
          )}
          {currentTab === "BOM" && (
            <Button variant="contained" onClick={handleSendBomToPlm}>
              Send BOM to PLM
            </Button>
          )}
          {currentTab === "Estimation" && (
            <UnitToggle isMetric={isMetric} onChange={handleUnitToggle} />
          )}
        </Box>

        {!hasRevision ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Select a revision in the header to load its estimation data.
            </Typography>
          </Box>
        ) : currentTab === "Estimation" ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              width: "100%",
              minWidth: 0,
              pt: 1,
            }}
          >
            {renderEstimationPanel(
              firstPanelColumns,
              estimationTable[selectedEstimationRevision]?.Estimation || [],
              "Estimation",
              firstPanelScrollRef,
              syncPanelScroll(firstPanelScrollRef, secondPanelScrollRef),
            )}

            {showEstimatedValues &&
              renderEstimationPanel(
                secondPanelColumns,
                estimationTable[selectedEstimationRevision]?.Estimation || [],
                "Estimated values",
                secondPanelScrollRef,
                syncPanelScroll(secondPanelScrollRef, firstPanelScrollRef),
              )}
          </Box>
        ) : (
        <MaterialReactTable
          {...mrtSurfaceProps}
          columns={
            columns(
              allTables,
              selectedSpecsTable,
              handleFieldUpdate,
              handleDeleteRow,
              handleSummaryRateUpdate,
              totalSummary,
              setTotalSummary,
              isMetric,
              getOptions,
              handleSpecSelect,
            )[currentTab]
          }
          data={estimationTable[selectedEstimationRevision]?.[currentTab] || []}
          enableTopToolbar={true}
          enableStickyHeader={true}
          enableSorting={false}
          enableColumnOrdering={false}
          enableColumnDragging={false}
          muiTableContainerProps={{ sx: { maxHeight: 600 } }}
          muiTablePaperProps={{ sx: { boxShadow: "none" } }}
          muiTableBodyCellProps={{
            sx: {
              borderRight: "1px solid",
              borderColor: "divider",
              fontSize: typographyScale.fontSize.sm,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "background.default",
              color: "text.primary",
              fontWeight: typographyScale.fontWeight.semibold,
              fontSize: typographyScale.fontSize.xs,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
          initialState={{ pagination: { pageSize: 50, pageIndex: 0 } }}
          state={{
            // ← add this back
            ...(currentTab === "BOM" && {
              columnOrder: [
                "ItemNo",
                "Material Description",
                "Qty",
                "length",
                "Unit",
                "Weight",
              ],
            }),
            ...(currentTab === "Summary" && {
              columnOrder: [
                "Material Description",
                "length", // Req. Qty
                "procuredQty", // Procured Qty
                "Unit",
                "unitCost", // Unit Cost $
                "totalCost", // Total Cost $
                "totalFabHrs", // Total Fab (hrs)
              ],
            }),
          }}
        />
        )}
      </Box>

      {currentTab === "Summary" && <TotalSummaryTable />}
    </Box>
  );
}
